import { create } from 'zustand';
import type { AssetKind, CustomerType, SearchDomain, TaskPriority, TaskStatus } from '@shared/types';

export type QuickAddDomain = Extract<SearchDomain, 'projects' | 'leads' | 'tasks' | 'customers' | 'resources' | 'contracts' | 'invoices'>;

export type QuickAddStatus = 'idle' | 'submitting' | 'error';

export interface ProjectQuickAddDraft {
  name: string;
  customerId: string;
}

export interface LeadQuickAddDraft {
  name: string;
  companyName: string;
  contactName: string;
  phone: string;
  email: string;
  currentWebsiteUrl: string;
  city: string;
  source: string;
  customerCategoryId: string;
  tags: string;
}

export interface TaskQuickAddDraft {
  title: string;
  description: string;
  status: TaskStatus;
  projectId: string;
  dueAt: string;
  priority: TaskPriority | '';
}

export interface CustomerQuickAddDraft {
  name: string;
  type: CustomerType;
}

export interface ResourceQuickAddDraft {
  name: string;
  kind: AssetKind;
  projectId: string;
  customerId: string;
  mimeType: string;
  storagePath: string;
}

export interface ContractQuickAddDraft {
  title: string;
  customerId: string;
  projectId: string;
}

export interface InvoiceQuickAddDraft {
  customerId: string;
  projectId: string;
}

export type QuickAddDraftsByDomain = {
  projects: ProjectQuickAddDraft;
  leads: LeadQuickAddDraft;
  tasks: TaskQuickAddDraft;
  customers: CustomerQuickAddDraft;
  resources: ResourceQuickAddDraft;
  contracts: ContractQuickAddDraft;
  invoices: InvoiceQuickAddDraft;
};

export const defaultQuickAddDrafts: QuickAddDraftsByDomain = {
  projects: {
    name: '',
    customerId: ''
  },
  leads: {
    name: '',
    companyName: '',
    contactName: '',
    phone: '',
    email: '',
    currentWebsiteUrl: '',
    city: '',
    source: '',
    customerCategoryId: '',
    tags: ''
  },
  tasks: {
    title: '',
    description: '',
    status: 'backlog',
    projectId: '',
    dueAt: '',
    priority: ''
  },
  customers: {
    name: '',
    type: 'company'
  },
  resources: {
    name: '',
    kind: 'document',
    projectId: '',
    customerId: '',
    mimeType: '',
    storagePath: ''
  },
  contracts: {
    title: '',
    customerId: '',
    projectId: ''
  },
  invoices: {
    customerId: '',
    projectId: ''
  }
};

export function createQuickAddDraft<D extends QuickAddDomain>(domain: D): QuickAddDraftsByDomain[D] {
  return structuredClone(defaultQuickAddDrafts[domain]);
}

interface QuickAddStore {
  isOpen: boolean;
  defaultDomain: QuickAddDomain;
  activeDomain: QuickAddDomain;
  draftsByDomain: QuickAddDraftsByDomain;
  status: QuickAddStatus;
  error: string | null;
  open: (domain: QuickAddDomain) => void;
  close: () => void;
  setActiveDomain: (domain: QuickAddDomain) => void;
  updateDraft: <D extends QuickAddDomain>(domain: D, patch: Partial<QuickAddDraftsByDomain[D]>) => void;
  resetDraft: (domain: QuickAddDomain) => void;
  setError: (error: string | null) => void;
  setStatus: (status: QuickAddStatus) => void;
}

export const useQuickAddStore = create<QuickAddStore>((set) => ({
  isOpen: false,
  defaultDomain: 'projects',
  activeDomain: 'projects',
  draftsByDomain: structuredClone(defaultQuickAddDrafts),
  status: 'idle',
  error: null,
  open: (domain) =>
    set((state) => ({
      isOpen: true,
      defaultDomain: domain,
      activeDomain: domain,
      status: 'idle',
      error: null,
      draftsByDomain: state.draftsByDomain
    })),
  close: () =>
    set((state) => ({
      isOpen: false,
      activeDomain: state.defaultDomain,
      status: 'idle',
      error: null
    })),
  setActiveDomain: (activeDomain) => set({ activeDomain, status: 'idle', error: null }),
  updateDraft: (domain, patch) =>
    set((state) => ({
      draftsByDomain: {
        ...state.draftsByDomain,
        [domain]: {
          ...state.draftsByDomain[domain],
          ...patch
        }
      }
    })),
  resetDraft: (domain) =>
    set((state) => ({
      draftsByDomain: {
        ...state.draftsByDomain,
        [domain]: createQuickAddDraft(domain)
      }
    })),
  setError: (error) => set({ error }),
  setStatus: (status) => set({ status })
}));
